import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, X, ExternalLink, Link2, ClipboardPaste } from "lucide-react";
import { usePasteImage, pasteFromClipboard } from "@/lib/pasteImage";

export default function ImageSearchModal({ open, onClose, onSelect, defaultQuery = "", barcode = "" }) {
  const [q, setQ] = useState(defaultQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pasteUrl, setPasteUrl] = useState("");
  const [hasGoogle, setHasGoogle] = useState(false);

  const search = async (e, query) => {
    e?.preventDefault();
    const term = (query ?? q).trim();
    if (term.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/image-search`, { params: { q: term, ...(barcode ? { barcode } : {}) } });
      setResults(data.results || []);
      setTotal(data.total || 0);
      setHasGoogle(!!data.google);
      if (!data.results?.length) toast.info("Sin resultados, prueba otra búsqueda");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setQ(defaultQuery);
      setResults([]);
      setTotal(0);
      setPasteUrl("");
      if (defaultQuery?.trim().length >= 2) search(null, defaultQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (url) => { onSelect(url); onClose(); };
  usePasteImage(open, pick);

  if (!open) return null;

  const googleUrl = `https://www.google.com/search?tbm=isch&gl=py&hl=es&q=${encodeURIComponent(q || defaultQuery)}`;

  const usePaste = () => {
    const u = pasteUrl.trim();
    if (!/^https?:\/\//i.test(u)) { toast.error("Pegá una URL de imagen válida (https://...)"); return; }
    onSelect(u); onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-testid="image-search-modal">
      <div className="card-surface w-full max-w-3xl max-h-[88vh] flex flex-col p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-xl font-bold">Buscar imagen en internet</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={search} className="flex gap-2 mb-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="image-search-input" autoFocus
            placeholder="Ej: cappuccino, croissant…"
            className="flex-1 h-11 px-4 rounded-lg bg-secondary border border-border focus:border-primary focus:outline-none" />
          <button type="submit" disabled={loading} data-testid="image-search-go"
            className="h-11 px-5 rounded-lg bg-primary text-primary-foreground font-semibold flex items-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
          </button>
          {!hasGoogle && (
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid="google-images-link"
              className="h-11 px-4 rounded-lg border border-border hover:bg-accent font-semibold flex items-center gap-2 text-sm">
              <ExternalLink className="w-4 h-4" /> Google
            </a>
          )}
        </form>
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Link2 className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <input value={pasteUrl} onChange={(e) => setPasteUrl(e.target.value)} data-testid="image-paste-url"
              placeholder="…o pegá aquí la URL de una imagen (clic derecho → Copiar dirección de imagen)"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none" />
          </div>
          <button onClick={usePaste} data-testid="image-paste-use" className="h-10 px-4 rounded-lg border border-primary/50 text-primary text-sm font-semibold hover:bg-primary/10">Usar</button>
          <button onClick={() => pasteFromClipboard(pick)} data-testid="image-paste-clipboard" className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-2 hover:bg-primary/90"><ClipboardPaste className="w-4 h-4" /> Pegar imagen</button>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-2 mb-4" data-testid="image-paste-hint">Tip: en Google Imágenes hacé clic derecho sobre la foto → <b>Copiar imagen</b>, y acá presioná <b>Ctrl+V</b> o "Pegar imagen".</p>
        {loading && <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Buscando imágenes…</div>}
        {!loading && results.length > 0 && <p className="text-xs text-muted-foreground mb-2">{total} resultados — hacé clic en una imagen para usarla</p>}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 overflow-y-auto">
          {results.map((r, i) => (
            <button key={i} onClick={() => { onSelect(r.image); onClose(); }} data-testid={`image-result-${i}`}
              className="relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary hover:neon-glow transition-all">
              {r.src !== "bing" && r.src !== "ddg" && <span className="absolute top-1 left-1 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/70 text-white">{r.src === "barcode" ? "CÓDIGO" : "GOOGLE"}</span>}
              <img src={r.thumbnail || r.image} alt={r.title} className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.opacity = 0.2; }} loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
